import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logbookApi, photoApi } from '../api/logbookApi';
import TopBar from '../components/TopBar';
import NewEntryModal from '../components/NewEntryModal';
import './LogbookDetailPage.css';
import EditEntryModal from '../components/EditEntryModal';
import { useAuth } from '../context/AuthContext';
import PhotoCapture from '../components/PhotoCapture';
import { useOffline } from '../context/OfflineContext';
import OfflineBanner from '../components/OfflineBanner';
import ProximityDetector from '../components/ProximityDetector';
import ArrivalSuggestionModal from '../components/ArrivalSuggestionModal';

const STATUS_CONFIG = {
  open:   { label: 'Nyitott', color: '#2bb344', bg: '#e8f9eb', icon: 'ti-circle-check' },
  saved:  { label: 'Mentett', color: '#b8862e', bg: '#fdf3e3', icon: 'ti-pause' },
  closed: { label: 'Lezárt',  color: '#8a958c', bg: '#f0f2f0', icon: 'ti-lock' },
};

const CATEGORY_CONFIG = {
  other:    { label: 'Egyéb / parkoló', icon: 'ti-parking',        color: '#8a958c' },
  pickup:   { label: 'Felrakó',         icon: 'ti-package',        color: '#2bb344' },
  delivery: { label: 'Lerakó',          icon: 'ti-package-export', color: '#b8862e' },
  customs:  { label: 'Vámkezelés',      icon: 'ti-building-bank',  color: '#9b59b6' },
  refuel:   { label: 'Tankolás',        icon: 'ti-gas-station',    color: '#d9342b' },
};

function formatDateTime(dt) {
  if (!dt) return '–';
  return new Date(dt).toLocaleString('hu-HU', {
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function LogbookDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [logbook, setLogbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const lastEntryRef = useRef(null);
  const [photoEntry, setPhotoEntry] = useState(null);
  const { isOnline, cacheLogbook, getCachedLogbook, getPendingEntries } = useOffline();
  const [pendingEntries, setPendingEntries] = useState([]);
  const [photoCounts, setPhotoCounts] = useState({});
  const [suggestedStation, setSuggestedStation] = useState(null);
  const [suggestedDistance, setSuggestedDistance] = useState(null);
  const [prefilledEntry, setPrefilledEntry] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchPhotoCounts = async (entries) => {
    const counts = {};
    for (const entry of entries) {
      if (['pickup', 'delivery'].includes(entry.category) && entry.order_identifier) {
        const categoryLabel = entry.category === 'pickup' ? 'Felrakó' : 'Lerakó';
        try {
          const res = await photoApi.getCount(entry.order_identifier, categoryLabel);
          counts[entry.id] = res.data.count;
        } catch {
          counts[entry.id] = 0;
        }
      }
    }
    setPhotoCounts(counts);
  };

  const fetchLogbook = async () => {
    try {
      if (isOnline) {
        const res = await logbookApi.get(id);
        setLogbook(res.data);
        await cacheLogbook(res.data);
        fetchPhotoCounts(res.data.entries);
      } else {
        const cached = await getCachedLogbook(parseInt(id));
        if (cached) {
          setLogbook(cached);
        } else {
          setError('Offline módban ez a menetlevél nem érhető el.');
        }
      }
      const pending = await getPendingEntries(parseInt(id));
      setPendingEntries(pending);
    } catch {
      setError('Nem sikerült betölteni a menetlevelet.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogbook(); }, [id]);

  useEffect(() => {
    if (!logbook) return;
    if (logbook.status === 'closed') return;
    const timer = setTimeout(() => {
      if (lastEntryRef.current) {
        lastEntryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [logbook, pendingEntries]);

  useEffect(() => {
    const handleSyncCompleted = () => fetchLogbook();
    window.addEventListener('sync-completed', handleSyncCompleted);
    return () => window.removeEventListener('sync-completed', handleSyncCompleted);
  }, []);

  const isActive = logbook?.status === 'open' || logbook?.status === 'saved';

  const handleNewEntry = () => {
    const allEntries = [...(logbook.entries || []), ...pendingEntries];
    if (allEntries.length > 0) {
      const lastEntry = allEntries[allEntries.length - 1];
      if (!lastEntry.departure) {
        alert('Az utolsó bejegyzésnél hiányzik a távozás időpontja! Előbb töltsd ki azt.');
        return;
      }
    }
    setIsSaving(true);
    setShowEntryModal(true);
  };

  const handleStationFound = useCallback((station, distance) => {
    if (!isActive || showEntryModal || editingEntry || isSaving) return;
    setSuggestedStation(station);
    setSuggestedDistance(distance);
  }, [isActive, showEntryModal, editingEntry, isSaving]);

  const handleArrivalConfirm = (station) => {
    setSuggestedStation(null);
    setIsSaving(true);
    setPrefilledEntry({
      category: station.category === 'Felrakó' ? 'pickup' :
                station.category === 'Lerakó' ? 'delivery' : 'customs',
      orderstation_id: station.id,
      city: station.city,
      country: station.country,
    });
    setShowEntryModal(true);
  };

  const handleClose = async () => {
    if (!window.confirm('Biztosan lezárod a menetlevelet?')) return;
    setClosing(true);
    try {
      await logbookApi.close(id);
      navigate('/');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Nem sikerült lezárni a menetlevelet.';
      alert(errorMsg);
    } finally {
      setClosing(false);
    }
  };

  const handleCloseWithoutRefuel = async () => {
    if (!window.confirm('Biztosan lezárod tankolás nélkül?')) return;
    setClosing(true);
    try {
      await logbookApi.closeWithoutRefuel(id);
      navigate('/');
    } catch {
      alert('Nem sikerült lezárni a menetlevelet.');
    } finally {
      setClosing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Biztosan törlöd ezt a menetlevelet? Ez a művelet nem vonható vissza!')) return;
    setDeleting(true);
    try {
      await logbookApi.delete(id);
      navigate('/');
    } catch {
      alert('Nem sikerült törölni a menetlevelet.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return (
    <div className="app-shell">
      <TopBar />
      <div className="app-content"><div className="loading-box">Betöltés...</div></div>
    </div>
  );

  if (error) return (
    <div className="app-shell">
      <TopBar />
      <div className="app-content"><div className="error-box">{error}</div></div>
    </div>
  );

  const statusCfg = STATUS_CONFIG[logbook.status];

  return (
    <div className="app-shell">
      <TopBar />
      <OfflineBanner />
      <div className="app-content">

        {/* Fejléc */}
        <div className="detail-header">
          <div className="detail-header-top">
            <button className="btn-back" onClick={() => navigate('/')}>
              <i className="ti ti-arrow-left"></i>
            </button>
            <div className="detail-header-info">
              <div className="detail-vehicle">
                <i className="ti ti-truck"></i>
                {logbook.vehicle_plate}
              </div>
              <div className="detail-identifier">{logbook.identifier}</div>
            </div>
            <span className="status-badge" style={{ color: statusCfg.color, background: statusCfg.bg }}>
              <i className={`ti ${statusCfg.icon}`}></i>
              {statusCfg.label}
            </span>
          </div>

          {user?.is_staff && (
            <div className="detail-admin-actions">
              <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                <i className="ti ti-trash"></i>
                {deleting ? 'Törlés...' : 'Menetlevél törlése'}
              </button>
            </div>
          )}
        </div>

        {/* Bejegyzések listája */}
        {logbook.entries.length === 0 && pendingEntries.length === 0 ? (
          <div className="empty-state">
            <i className="ti ti-clipboard-text"></i>
            <p>Még nincs bejegyzés.</p>
            <span>Add hozzá az első bejegyzést!</span>
          </div>
        ) : (
          <div className="entries-timeline">
            {logbook.entries.map((entry, idx) => {
              const cfg = CATEGORY_CONFIG[entry.category] || {};
              const isLast = idx === logbook.entries.length - 1 && pendingEntries.length === 0;
              const missingDeparture = isLast && !entry.departure && logbook.status !== 'closed';

              const hasEmptyFields =
                !entry.arrival ||
                !entry.departure ||
                (entry.category === 'refuel' && !entry.diesel);

              const isEditable = isActive && hasEmptyFields;
              const hasCamera = isOnline && isActive && ['pickup', 'delivery'].includes(entry.category);

              return (
                <div
                  className="entry-item"
                  key={entry.id}
                  ref={isLast ? lastEntryRef : null}
                >
                  <div className="entry-line">
                    <div className="entry-dot" style={{ background: cfg.color }}></div>
                    {!isLast && <div className="entry-connector"></div>}
                  </div>
                  <div className={`entry-card ${missingDeparture ? 'entry-card-alert' : ''}`}>
                    <div className="entry-card-top">
                      <span className="entry-label" style={{ color: cfg.color }}>
                        <i className={`ti ${cfg.icon}`}></i>
                        {cfg.label}
                      </span>
                      <span className="entry-km-badge">
                        {entry.km?.toLocaleString('hu-HU')} km
                      </span>
                    </div>
                    <div className="entry-city-name">
                      {entry.city}
                      {entry.country && (
                        <span className="entry-country"> ({entry.country})</span>
                      )}
                    </div>
                    <div className="entry-times">
                      {entry.arrival ? (
                        <span><i className="ti ti-login"></i> {formatDateTime(entry.arrival)}</span>
                      ) : (
                        <span className="entry-missing"><i className="ti ti-login"></i> Érkezés hiányzik</span>
                      )}
                      {entry.departure ? (
                        <span><i className="ti ti-logout"></i> {formatDateTime(entry.departure)}</span>
                      ) : (
                        <span className="entry-missing"><i className="ti ti-logout"></i> Távozás hiányzik</span>
                      )}
                    </div>
                    {entry.category === 'refuel' && (
                      <div className="entry-refuel">
                        {entry.diesel
                          ? <span><i className="ti ti-droplet"></i> Dízel: {entry.diesel} l</span>
                          : <span className="entry-missing"><i className="ti ti-droplet"></i> Dízel hiányzik</span>
                        }
                        {entry.adblue
                          ? <span><i className="ti ti-droplet-half-2"></i> AdBlue: {entry.adblue} l</span>
                          : null
                        }
                      </div>
                    )}

                    {(isEditable || hasCamera) && (
                      <div className="entry-actions">
                        {isEditable && (
                          <button
                            className="btn-entry-action btn-entry-edit"
                            onClick={() => {
                              setIsSaving(true);
                              setEditingEntry(entry);
                            }}
                          >
                            <i className="ti ti-pencil"></i>
                            <span>Szerkesztés</span>
                          </button>
                        )}
                        {hasCamera && (
                          <button
                            className="btn-entry-action btn-entry-camera"
                            onClick={() => setPhotoEntry(entry)}
                          >
                            <i className="ti ti-camera"></i>
                            <span>
                              Fotó
                              {photoCounts[entry.id] > 0 && (
                                <span className="photo-count-badge"> ({photoCounts[entry.id]} db)</span>
                              )}
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Pending (offline) bejegyzések */}
            {pendingEntries.map((entry, idx) => {
              const cfg = CATEGORY_CONFIG[entry.category] || {};
              const isLast = idx === pendingEntries.length - 1;
              const canEdit = !entry.departure;

              return (
                <div
                  className="entry-item"
                  key={`pending-${idx}`}
                  ref={isLast ? lastEntryRef : null}
                >
                  <div className="entry-line">
                    <div className="entry-dot" style={{ background: '#b8862e' }}></div>
                    {!isLast && <div className="entry-connector"></div>}
                  </div>
                  <div className="entry-card entry-card-pending">
                    <div className="entry-card-top">
                      <span className="entry-label" style={{ color: cfg.color || '#b8862e' }}>
                        <i className={`ti ${cfg.icon || 'ti-clock'}`}></i>
                        {cfg.label || entry.category}
                      </span>
                      <span className="pending-entry-badge">
                        <i className="ti ti-wifi-off"></i> Függőben
                      </span>
                    </div>
                    <div className="entry-city-name">
                      {entry.city}
                      {entry.country && (
                        <span className="entry-country"> ({entry.country})</span>
                      )}
                    </div>
                    <div className="entry-times">
                      {entry.arrival && (
                        <span><i className="ti ti-login"></i> {formatDateTime(entry.arrival)}</span>
                      )}
                      {entry.departure && (
                        <span><i className="ti ti-logout"></i> {formatDateTime(entry.departure)}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#b8862e', marginTop: 6 }}>
                      <i className="ti ti-clock"></i> Szinkronizálásra vár
                    </div>
                    {canEdit && (
                      <div className="entry-actions">
                        <button
                          className="btn-entry-action btn-entry-edit"
                          onClick={() => {
                            setIsSaving(true);
                            setEditingEntry({ ...entry, isPending: true });
                          }}
                        >
                          <i className="ti ti-pencil"></i>
                          <span>Szerkesztés</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Összesítő – csak lezárt menetlevélnél */}
            {logbook.status === 'closed' && logbook.summary && (
              <div className="logbook-summary">
                <h3 className="summary-title">
                  <i className="ti ti-chart-bar"></i> Összesítő
                </h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Nyitó km</span>
                    <span className="summary-value">
                      {logbook.summary.opening_km?.toLocaleString('hu-HU')} km
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Záró km</span>
                    <span className="summary-value">
                      {logbook.summary.closing_km?.toLocaleString('hu-HU')} km
                    </span>
                  </div>
                  <div className="summary-item highlight">
                    <span className="summary-label">Megtett távolság</span>
                    <span className="summary-value">
                      {logbook.summary.distance?.toLocaleString('hu-HU')} km
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Tankolt dízel</span>
                    <span className="summary-value">
                      {logbook.summary.total_diesel} l
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Tankolt AdBlue</span>
                    <span className="summary-value">
                      {logbook.summary.total_adblue} l
                    </span>
                  </div>
                  <div className="summary-item highlight">
                    <span className="summary-label">Fogyasztás</span>
                    <span className="summary-value">
                      {logbook.summary.consumption
                        ? `${logbook.summary.consumption} l/100km`
                        : '–'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Akció gombok */}
        {isActive && (
          <div className="detail-actions">
            <button className="btn-add-entry" onClick={handleNewEntry}>
              <i className="ti ti-plus"></i> Új bejegyzés
            </button>
            {isOnline && (
              <div className="detail-close-actions">
                <button className="btn-close-refuel" onClick={handleClose} disabled={closing}>
                  <i className="ti ti-gas-station"></i>
                  Lezárás tankolással
                </button>
                <button className="btn-close-no-refuel" onClick={handleCloseWithoutRefuel} disabled={closing}>
                  <i className="ti ti-lock"></i>
                  Lezárás tankolás nélkül
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Proximity detektor – szünetel mentés közben */}
      {isOnline && isActive && !isSaving && (
        <ProximityDetector onStationFound={handleStationFound} />
      )}

      {/* Érkezés felajánló modal */}
      {suggestedStation && (
        <ArrivalSuggestionModal
          station={suggestedStation}
          distance={suggestedDistance}
          onConfirm={handleArrivalConfirm}
          onDismiss={() => setSuggestedStation(null)}
        />
      )}

      {showEntryModal && (
        <NewEntryModal
          logbookId={id}
          prefilled={prefilledEntry}
          onClose={() => {
            setShowEntryModal(false);
            setPrefilledEntry(null);
            setIsSaving(false);
          }}
          onCreated={() => {
            setShowEntryModal(false);
            setPrefilledEntry(null);
            setIsSaving(false);
            fetchLogbook();
          }}
        />
      )}

      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          onClose={() => {
            setEditingEntry(null);
            setIsSaving(false);
          }}
          onSaved={() => {
            setEditingEntry(null);
            setIsSaving(false);
            fetchLogbook();
          }}
        />
      )}

      {photoEntry && (
        <PhotoCapture
          entry={photoEntry}
          logbook={logbook}
          onClose={() => {
            setPhotoEntry(null);
            if (logbook) fetchPhotoCounts(logbook.entries);
          }}
        />
      )}

    </div>
  );
}